import type { AudioRecorder } from "/js/AudioRecorder";
import { useThrottleFn } from "@vueuse/core";
import type { RecordingChunk } from "~vue/ChatDetail/types";
import { useMicConsent } from "~vue/composables/useMicConsent";
import { useTranscription } from "~vue/composables/useTranscription";
import { logError } from "~vue/utils/logUtils";
import { onMounted, onUnmounted, ref, watch } from "vue";

type UseRecorderProps = {
    recorder?: AudioRecorder;
};

// TODO: this is copied from implementation in UserProfile ProfileQuestionAnswer
// this should be adapted to be used there as well
export const useRecorder = ({ recorder }: UseRecorderProps = {}) => {
    const error = ref<string | null>(null);
    const { isTranscribing, transcribe } = useTranscription({
        source: "useRecorder",
        error,
        errorMessage: "Transcription failed. Please try again.",
    });
    const { micConsentBlocks } = useMicConsent();
    const isRecording = ref(false);
    const input = ref("");
    const isBelowSilenceThreshold = ref<boolean>(false);
    const recordingLevelSamplingRate = ref(100);
    const micPermissionGranted = ref(true);
    // True while a native browser permission prompt is expected to be visible (between
    // detecting `state === "prompt"` and `recorder.start()` resolving). Consumers can watch
    // this to surface explanatory UI alongside the native dialog.
    const isAwaitingPermission = ref(false);
    // True from Stop until the transcript (or silence) arrives. The recorder emits `finish`
    // asynchronously and transcription starts after that, so neither isRecording nor
    // isTranscribing covers the gap.
    const isFinalizing = ref(false);

    const handleSilenceThresholdFromLevel = useThrottleFn((level) => {
        isBelowSilenceThreshold.value = recorder?.isBelowSilenceThreshold(level) ?? false;
        recordingLevelSamplingRate.value = isBelowSilenceThreshold.value ? 200 : 4000;
    }, recordingLevelSamplingRate.value);

    /**
     * Queries the current mic permission state, refreshes `micPermissionGranted`, and returns
     * the raw state for callers that need to react to a `"prompt"` transition. Returns null
     * if the Permissions API is unavailable (e.g. some embedded webviews).
     */
    const refreshPermissionState = async (): Promise<PermissionState | null> => {
        try {
            const micPermission = await navigator.permissions.query({ name: "microphone" });
            // If microphone permission is denied hook clients can decide to preemptively hide recording buttons.
            // In MS 365 Copilot where permission cannot be granted we do not give the choice to record in Role Play,
            // but do elsewhere with a popup that explains the microphone is not available and switches to text.
            micPermissionGranted.value = micPermission.state !== "denied";
            return micPermission.state;
        } catch (e) {
            logError(e);
            return null;
        }
    };

    const handleStop = ({ isAborted }: { isAborted: boolean }) => {
        isFinalizing.value = !isAborted;
    };

    const handleSilence = () => {
        isFinalizing.value = false;
    };

    onMounted(async () => {
        recorder?.on("level", handleSilenceThresholdFromLevel);
        // @ts-expect-error - AudioRecorder is not typed yet
        recorder?.on("stop", handleStop);
        recorder?.on("silence", handleSilence);
        // @ts-expect-error - AudioRecorder is not typed yet
        recorder?.on("finish", handleTranscribe);

        await refreshPermissionState();
    });

    onUnmounted(() => {
        recorder?.off("level", handleSilenceThresholdFromLevel);
        // @ts-expect-error - AudioRecorder is not typed yet
        recorder?.off("stop", handleStop);
        recorder?.off("silence", handleSilence);
    });

    watch(input, () => {
        if (error.value) {
            error.value = null;
        }
    });

    const handleRecord = async () => {
        error.value = null;
        if (isRecording.value) {
            isRecording.value = false;
            recorder?.stop();
            return;
        }

        if (recorder?.isRecording) {
            return;
        }

        if (micConsentBlocks(handleRecord)) {
            return;
        }

        // Re-attach the finish listener in case it was removed from a previous recording
        // @ts-expect-error - AudioRecorder is not typed yet
        recorder?.on("finish", handleTranscribe);

        // Check just-in-time so callers can surface UI while the native permission dialog is open.
        // Permissions API failures (some embedded webviews) are swallowed inside refreshPermissionState.
        const permissionState = await refreshPermissionState();
        const needsPrompt = permissionState === "prompt";
        if (needsPrompt) {
            isAwaitingPermission.value = true;
        }

        try {
            isRecording.value = true;
            await recorder?.start();
        } catch (e) {
            logError(`Failed to start recording: ${e}`);
            // @ts-expect-error - AudioRecorder is not typed yet
            recorder?.off("finish", handleTranscribe);
            isRecording.value = false;
            if (e instanceof Error && e.name === "NotAllowedError") {
                error.value = "Please allow microphone access to record your answer.";
            } else if (e instanceof Error && e.name === "NotSupportedError") {
                error.value = "Microphone disconnected. Please reconnect your device or switch to text.";
            } else {
                error.value = "Failed to start recording. Please try again.";
            }
        } finally {
            if (needsPrompt) {
                isAwaitingPermission.value = false;
            }
        }
    };

    const handleTranscribe = async ({ blob, audioStats }: Pick<RecordingChunk, "blob" | "audioStats">) => {
        try {
            const transcript = await transcribe({ blob, audioStats });
            if (transcript) {
                input.value = transcript;
            }
        } finally {
            // @ts-expect-error - AudioRecorder is not typed yet
            recorder?.off("finish", handleTranscribe);
            isRecording.value = false;
            isFinalizing.value = false;
        }
    };

    return {
        error,
        isTranscribing,
        isRecording,
        isBelowSilenceThreshold,
        input,
        handleRecord,
        handleTranscribe,
        micPermissionGranted,
        isAwaitingPermission,
        isFinalizing,
    };
};
